import React, { useEffect, useMemo, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { usersDropdownListFetch } from "actions";
import {
  JOB_EMAIL_CATEGORY,
  JOB_EMAIL_CONFIGS,
  emptyRecipientValue,
} from "../../constants/jobEmailConfig";
import {
  fetchSetupConfigurations,
  saveSetupConfigurations,
} from "../../actions";
import JobEmailRecipientSelect, {
  optionsToRecipientValue,
  recipientValueToOptions,
} from "./JobEmailRecipientSelect";

const normalizeSavedItems = (items = []) => {
  const map = {};

  items.forEach((item) => {
    const key = item?.config_key || item?.key;
    if (!key) return;

    const value = item?.config_value || item?.value || {};
    map[key] = {
      user_ids: Array.isArray(value.user_ids) ? value.user_ids : [],
      custom_emails: Array.isArray(value.custom_emails)
        ? value.custom_emails
        : [],
    };
  });

  return map;
};

const JobEmailConfigurationTab = () => {
  const dispatch = useDispatch();
  const session = useSelector((state) => state.session);
  const userState = useSelector((state) => state.userState);
  const setupConfigState = useSelector((state) => state.setupConfigState);

  const objectViewedId = session?.current_page_permissions?.object_id || "";
  const savedItems = setupConfigState?.configsByCategory?.[JOB_EMAIL_CATEGORY] || [];
  const validationError = setupConfigState?.validation_error || {};

  const [formValues, setFormValues] = useState({});
  const [selectValues, setSelectValues] = useState({});

  const userList = userState?.usersDropdownList || [];

  useEffect(() => {
    dispatch(
      usersDropdownListFetch({
        object_viewed_id: objectViewedId,
        usersToshow: "internal",
      })
    );
    dispatch(
      fetchSetupConfigurations(JOB_EMAIL_CATEGORY, {
        object_viewed_id: objectViewedId,
      })
    );
  }, [dispatch, objectViewedId]);

  useEffect(() => {
    const normalized = normalizeSavedItems(savedItems);
    const nextFormValues = {};
    const nextSelectValues = {};

    JOB_EMAIL_CONFIGS.forEach((job) => {
      const recipientValue = normalized[job.key] || emptyRecipientValue();
      nextFormValues[job.key] = recipientValue;
      nextSelectValues[job.key] = recipientValueToOptions(
        recipientValue,
        userList
      );
    });

    setFormValues(nextFormValues);
    setSelectValues(nextSelectValues);
  }, [savedItems, userList]);

  const hasChanges = useMemo(() => {
    const normalizedSaved = normalizeSavedItems(savedItems);
    return JOB_EMAIL_CONFIGS.some((job) => {
      const current = formValues[job.key] || emptyRecipientValue();
      const saved = normalizedSaved[job.key] || emptyRecipientValue();
      return JSON.stringify(current) !== JSON.stringify(saved);
    });
  }, [formValues, savedItems]);

  const handleRecipientChange = (jobKey, options) => {
    const recipientValue = optionsToRecipientValue(options);
    setFormValues((prev) => ({
      ...prev,
      [jobKey]: recipientValue,
    }));
    setSelectValues((prev) => ({
      ...prev,
      [jobKey]: options,
    }));
  };

  const handleSave = () => {
    const items = JOB_EMAIL_CONFIGS.map((job) => ({
      config_key: job.key,
      config_value: formValues[job.key] || emptyRecipientValue(),
    }));

    dispatch(
      saveSetupConfigurations(JOB_EMAIL_CATEGORY, items, objectViewedId)
    );
  };

  const getFieldError = (jobKey) => {
    const fieldError =
      validationError?.[`items.${jobKey}`] ||
      validationError?.[jobKey] ||
      validationError?.[`${jobKey}.custom_emails`];

    if (Array.isArray(fieldError)) return fieldError[0];
    if (typeof fieldError === "string") return fieldError;
    return "";
  };

  return (
    <div className="setup-content-div">
      <div className="d-flex justify-content-end mb-3">
        <button
          type="button"
          className="n-u-btn"
          onClick={handleSave}
          disabled={setupConfigState?.loading || !hasChanges}
        >
          Save Configuration
        </button>
      </div>

      {JOB_EMAIL_CONFIGS.map((job, index) => (
        <div
          key={job.key}
          className={`setup-config-row ${index < JOB_EMAIL_CONFIGS.length - 1 ? "setup-config-row-border" : ""}`}
        >
          <Row className="align-items-center g-3">
            <Col lg={5} md={12}>
              <div className="setup-config-label-wrap">
                <h5 className="setup-config-title mb-1">{job.title}</h5>
                <p className="setup-config-description mb-0">{job.description}</p>
                <span className="setup-config-job-code">Job: {job.jobCode}</span>
              </div>
            </Col>
            <Col lg={7} md={12}>
              <JobEmailRecipientSelect
                value={selectValues[job.key] || []}
                onChange={(options) => handleRecipientChange(job.key, options)}
                userOptions={userList}
                label="Select Recipients"
                error={getFieldError(job.key)}
                isDisabled={setupConfigState?.loading}
              />
            </Col>
          </Row>
        </div>
      ))}
    </div>
  );
};

export default JobEmailConfigurationTab;
